import { ReactElement, useState } from "react";
import { TextField, Divider, Button } from "@mui/material";
import MethodInfo from "./FormInfo";

export interface UMAPFormProps {
  onSubmit?: (data: UMAPData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface UMAPData {
  min_dist: number;
  n_neighbors: number;
  n_pcs: number | null;
}

const UMAPForm = ({
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: UMAPFormProps): ReactElement => {
  const [formData, setFormData] = useState<UMAPData>({
    min_dist: 0.5,
    n_neighbors: 15,
    n_pcs: 50,
  });

  const handleInputChange = (
    field: keyof UMAPData,
    value: string | number | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as any,
    }));
  };

  const handleSubmit = () => {
    onSubmit?.(formData);
  };

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <MethodInfo
        title="UMAP parameters"
        message="The neighbor graph uses the selected neighbor count and principal components. UMAP uses the selected minimum distance and a fixed reproducibility seed of 0. The clustering step reuses this graph and embedding."
      />

      <Divider className="my-4" />

      {/* Form Fields */}
      <div className="space-y-4 flex-1">
        <TextField
          fullWidth
          label="Minimum Distance"
          type="number"
          value={formData.min_dist}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              handleInputChange("min_dist", 0);
            } else {
              const numValue = parseFloat(value);
              if (!isNaN(numValue) && numValue >= 0) {
                handleInputChange("min_dist", numValue);
              }
            }
          }}
          inputProps={{
            step: "any",
            min: 0,
          }}
          sx={{
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB",
              },
            },
          }}
        />

        <TextField
          fullWidth
          label="Number of Neighbors"
          type="number"
          value={formData.n_neighbors}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              handleInputChange("n_neighbors", 0);
            } else {
              const numValue = parseInt(value);
              if (!isNaN(numValue) && numValue >= 1) {
                handleInputChange("n_neighbors", numValue);
              }
            }
          }}
          inputProps={{
            step: 1,
            min: 1,
          }}
          sx={{
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB",
              },
            },
          }}
        />

        <TextField
          fullWidth
          label="Number of Principal Components"
          type="number"
          value={formData.n_pcs ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              handleInputChange("n_pcs", null);
            } else {
              const numValue = parseInt(value);
              if (!isNaN(numValue) && numValue >= 1) {
                handleInputChange("n_pcs", numValue);
              }
            }
          }}
          inputProps={{
            step: 1,
            min: 1,
          }}
          placeholder="None"
          sx={{
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB",
              },
            },
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between gap-3 mt-6">
        <Button
          variant="outlined"
          color="primary"
          onClick={onBack}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default UMAPForm;
